/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { ModalAwareScrollView } from '../modals/ModalAwareScrollView';
import {
  Platform,
  RefreshControl,
  StyleSheet,
  View,
  type ScrollViewProps,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { type KeyboardAwareScrollViewRef } from 'react-native-keyboard-controller';
import { colors, spacing, webOperationalScrollBindings } from '../../shared/theme/public-api';
import type { ScreenLayoutVariant } from './breakpoints';
import { useContentLayout } from './useContentLayout';

type Props = Omit<ScrollViewProps, 'contentContainerStyle'> & {
  children: React.ReactNode;
  contentContainerStyle?: StyleProp<ViewStyle>;
  variant?: ScreenLayoutVariant;
  /** Anula el maxWidth del variant (p. ej. wizard onboarding ancho). */
  contentMaxWidth?: number;
  /** Deja ver BrandWatermark en AuthScreenFrame (p. ej. onboarding con variant form). */
  transparentBackground?: boolean;
  /** Padding extra bajo el contenido cuando el teclado está abierto. */
  keyboardBottomPadding?: number;
  /** View del contenido scrolleable. */
  contentRef?: React.RefObject<View | null>;
  refreshing?: boolean;
  onRefresh?: () => void;
};

/**
 * Scroll container for root-stack modals. Centers and caps width on web so forms
 * are not full viewport width on large monitors.
 */
export const ModalScrollScreen = React.forwardRef<KeyboardAwareScrollViewRef, Props>(function ModalScrollScreen(
  {
    children,
    style,
    contentContainerStyle,
    variant = 'form',
    contentMaxWidth: contentMaxWidthOverride,
    transparentBackground = false,
    keyboardBottomPadding,
    contentRef,
    refreshing,
    onRefresh,
    ...scrollProps
  },
  ref,
) {
  const { contentMaxWidth: variantMaxWidth } = useContentLayout(variant);
  const contentMaxWidth = contentMaxWidthOverride ?? variantMaxWidth;

  const refreshControl =
    onRefresh != null ? (
      <RefreshControl
        refreshing={refreshing ?? false}
        onRefresh={onRefresh}
        colors={[colors.textSecondary]}
        tintColor={colors.textSecondary}
      />
    ) : undefined;

  /** Auth screens usan AuthScreenFrame + BrandWatermark; scroll opaco lo tapaba. */
  const scrollTransparent = variant === 'auth' || transparentBackground;

  return (
    <ModalAwareScrollView
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as any}
      {...webOperationalScrollBindings([
        styles.scroll,
        scrollTransparent ? styles.scrollTransparent : null,
        style,
      ])}
      contentContainerStyle={[styles.contentOuter, contentContainerStyle]}
      keyboardShouldPersistTaps="handled"
      bottomOffset={keyboardBottomPadding}
      refreshControl={refreshControl}
      {...scrollProps}
    >
      <View
        ref={contentRef}
        collapsable={false}
        style={[
          styles.inner,
          contentMaxWidth != null ? { maxWidth: contentMaxWidth, width: '100%' } : null,
        ]}
      >
        {children}
      </View>
    </ModalAwareScrollView>
  );
});

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scrollTransparent: {
    backgroundColor: 'transparent',
  },
  contentOuter: {
    flexGrow: 1,
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.xxl,
  },
  inner: {
    alignSelf: 'center',
    width: '100%',
    paddingTop: spacing.md,
    gap: spacing.md,
  },
});
